<template>
    <div class="page-contents">
        <component
            :is="globalStore.header.component"
            v-if="globalStore.header"
            :global="globalStore.global.content"
            :blok="globalStore.header"
        />
        <main>
            <component v-if="story" :is="story.content.component" :blok="story.content" />
        </main>
        <component
            :is="'Lazy' + globalStore.emailSubscription.component"
            v-if="globalStore.emailSubscription"
            :global="globalStore.global.content"
            :blok="globalStore.emailSubscription"
        />
        <component
            :is="'Lazy' + globalStore.footer.component"
            v-if="globalStore.footer"
            :global="globalStore.global.content"
            :blok="globalStore.footer"
        />
    </div>
</template>

<script setup lang="ts">
definePageMeta({
    middleware: ['underscore-redirect'],
});

import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';
import type { IShuttleLocationData } from '@nuxt-monorepo/ui/types/products/ShuttleProducts';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

// Constants
const config = useRuntimeConfig();
const globalStore: any = useGlobalStore();
const storyblokApi = useStoryblokApi();
const { locale } = useI18n();
const route: RouteLocationNormalizedLoaded = useRoute();
const airportSlug = Array.isArray(route.params.airport) ? route.params.airport[0] : route.params.airport || '';
const productSlug = Array.isArray(route.params.product) ? route.params.product[0] : route.params.product || '';
const pageSlug = `${config?.public.STORYBLOK_FOLDER}/pages/v2/shuttles/${airportSlug}/${productSlug}`;
const templateSlug = `${config?.public.STORYBLOK_FOLDER}/pages/v2/shuttles/shuttle-journey-template`;
const resolveRelations = [
    'Page.globals',
    'CoreSharedComponent.SharedComponent',
    'ImageGridAirports.Airports',
    'ImageGridAirportsV2.Airports',
    'ImageGrid.Airports',
    'Link_Section.entities',
    'PSFAirportPage.globals',
    'PSFAirportPage.AirportEntity',
    'PartnersContainer.Partners',
];

// Helpers

/** Returns airport code (lowercase) from airportSlug */
function getAirportCode(airportSlug: string): string {
    if (airportSlug.length <= 3) return null;
    return airportSlug.slice(-3).toLowerCase();
}

/** Returns product slug for API: replaces all dashes with underscores and removes "l_" prefix if present */
function getCleanedProductSlug(productSlug: string): string {
    // Remove "l-" prefix, then replace all remaining dashes with underscores
    return productSlug.replace(/^l-/, '');
}

/** Returns true if route should be blocked by config */
function isBlockedRoute(config: any, route: RouteLocationNormalizedLoaded): boolean {
    const blocked = config?.public.STORYBLOK_ROUTE_BLOCKED;
    if (!blocked) return false;
    return blocked.split(', ').some((block: string) => route.path.includes(block));
}

/** Returns object for deep replacements in storyblok content */
function getReplacements(locationData: IShuttleLocationData | null) {
    return {
        airportCode: locationData?.airport?.code ?? '',
        airportName: locationData?.airport?.name ?? '',
        airportSlug: airportSlug ?? '',
        locationName: locationData?.dropoff ?? locationData?.pickup ?? '',
        locationSlug: productSlug ?? '',
    };
}

/** Fetches a Storyblok story by slug */
async function fetchStory(slug: string) {
    return storyblokApi
        .get(`cdn/stories/${slug}`, {
            version: config.public.STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
            language: locale.value,
            resolve_relations: resolveRelations,
        })
        .then((res) => res?.data?.story || null)
        .catch(() => null);
}

// ---- MAIN LOGIC ----

// Get airport code
const airportCode = getAirportCode(airportSlug);
const cleanedProductSlug = getCleanedProductSlug(productSlug);
if (!airportCode || !cleanedProductSlug) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

// Get location data
const { data: locationDataRaw } = await useFetch<{ data: IShuttleLocationData }>('/api/airport-location', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { airport: airportCode, product: cleanedProductSlug, caller: 'apr' },
});
const locationData = locationDataRaw.value?.data || null;
provide('locationData', locationData);

// Get locations data
const { data: airportLocationsDataRaw } = await useFetch<{ data: IShuttleLocationData[] }>('/api/airport-location', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: { airport: airportCode?.toLowerCase(), q: cleanedProductSlug.charAt(0), caller: 'apr' },
});
const shuttleLocationsList = airportLocationsDataRaw.value?.data || null;
provide('shuttleLocationsList', shuttleLocationsList);

// Load Storyblok content
const storyData = (await fetchStory(pageSlug)) || (await fetchStory(templateSlug));

const story = ref(storyData);

// Blocked route or no content → error
if (isBlockedRoute(config, route) || !story?.value || !locationData?.slug) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

// Replace placeholders in Storyblok content
const replacements = getReplacements(locationData);
story.value.content = deepReplacePlaceholders(story.value.content, replacements);

// Handle SEO/meta and global store
if (story?.value) {
    setSeoMeta(story.value.content?.seo, config?.public, route);
    if (story.value.content?.globals) {
        globalStore.update(story.value.content.globals);
    }
}
</script>
